/**
 * Sealing a client organization's key to a firm user's public key, so the
 * Hub can store and relay it without being able to read it: an ephemeral
 * X25519 key agreement, HKDF-SHA256, then AES-GCM.
 *
 * Format: a version byte, the ephemeral public key (32), the nonce (12) and
 * the ciphertext. Version 2 binds the organization and key version into the
 * authenticated data, so a copy can't be passed off as another client's or
 * another version's; version 1 (without that) is still read.
 */
import { x25519 } from '@noble/curves/ed25519';
import { hkdf } from '@noble/hashes/hkdf';
import { sha256 } from '@noble/hashes/sha256';
import { base64ToBytes, bytesToBase64, concatBytes } from '~/util/bytes';
import { parseOrgSecret, uuidBytes } from '~/util/orgSigning';
const VERSION = 2;
const LEGACY_VERSION = 1;
const INFO = new TextEncoder().encode('zunox/org-key-grant/v1');
const KEY_BYTES = 32;
const NONCE_BYTES = 12;
function additionalData(version, context) {
    if (version === LEGACY_VERSION)
        return INFO;
    const keyVersion = new Uint8Array(4);
    new DataView(keyVersion.buffer).setInt32(0, context.keyVersion);
    return concatBytes(INFO, uuidBytes(context.orgId), keyVersion);
}
async function aesKey(shared, ephemeral, recipient, usage) {
    const raw = hkdf(sha256, shared, concatBytes(ephemeral, recipient), INFO, 32);
    return crypto.subtle.importKey('raw', raw, 'AES-GCM', false, [usage]);
}
/** Seals `secret` (an organization key, as pasted) to a public key. */
export async function sealSecret(recipientPublicKey, secret, context) {
    const recipient = base64ToBytes(recipientPublicKey);
    if (recipient.length !== KEY_BYTES)
        throw new Error('Not a recipient key.');
    const ephemeralPrivate = x25519.utils.randomPrivateKey();
    const ephemeral = x25519.getPublicKey(ephemeralPrivate);
    const shared = x25519.getSharedSecret(ephemeralPrivate, recipient);
    const nonce = crypto.getRandomValues(new Uint8Array(NONCE_BYTES));
    const ciphertext = await crypto.subtle.encrypt({
        name: 'AES-GCM',
        iv: nonce,
        additionalData: additionalData(VERSION, context),
    }, await aesKey(shared, ephemeral, recipient, 'encrypt'), new TextEncoder().encode(secret.trim()));
    return bytesToBase64(concatBytes(Uint8Array.of(VERSION), ephemeral, nonce, new Uint8Array(ciphertext)));
}
/** Opens a sealed key with the recipient's private key. Throws if it was
 * sealed to someone else, for another organization or version, or was
 * tampered with. */
export async function openSecret(privateKey, sealed, context) {
    const bytes = base64ToBytes(sealed);
    const header = 1 + KEY_BYTES + NONCE_BYTES;
    const version = bytes[0];
    if ((version !== VERSION && version !== LEGACY_VERSION) || bytes.length <= header) {
        throw new Error('This shared key is in a format this page does not know.');
    }
    const ephemeral = bytes.subarray(1, 1 + KEY_BYTES);
    const nonce = bytes.subarray(1 + KEY_BYTES, header);
    const recipient = x25519.getPublicKey(privateKey);
    const shared = x25519.getSharedSecret(privateKey, ephemeral);
    const plaintext = await crypto.subtle.decrypt({
        name: 'AES-GCM',
        iv: nonce,
        additionalData: additionalData(version, context),
    }, await aesKey(shared, ephemeral, recipient, 'decrypt'), bytes.subarray(header));
    return new TextDecoder().decode(plaintext);
}
/** Whether `secret` is the key for an organization whose current public
 * key is `publicKeyBase64`, so a wrong paste is never saved or shared. */
export function secretMatches(secret, publicKeyBase64) {
    try {
        const derived = x25519.getPublicKey(parseOrgSecret(secret).encryptionKey);
        return bytesToBase64(derived) === publicKeyBase64;
    }
    catch {
        return false;
    }
}
